import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateSupportThread } from "@/hooks/useSupportThreads";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const CATEGORIES = [
  { value: "account", label: "Account" },
  { value: "courses", label: "Courses" },
  { value: "mentorship", label: "Mentorship" },
  { value: "technical", label: "Technical" },
  { value: "other", label: "Other" },
];

interface NewRequestFormProps {
  onSuccess?: () => void;
}

export function NewRequestForm({ onSuccess }: NewRequestFormProps) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");

  const createThread = useCreateSupportThread();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    try {
      await createThread.mutateAsync({
        subject: subject.trim() || undefined,
        category: category || undefined,
        message: message.trim(),
      });
      toast.success("Support request submitted!");
      setSubject("");
      setCategory("");
      setMessage("");
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to submit request");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="subject">Subject (optional)</Label>
        <Input
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief description of your issue"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Category (optional)</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger>
            <SelectValue placeholder="Select a category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe your question or issue..."
          rows={5}
          required
        />
      </div>

      <Button type="submit" className="w-full" disabled={createThread.isPending}>
        {createThread.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            Submit Request
          </>
        )}
      </Button>
    </form>
  );
}
